// @ts-ignore
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AccessToken } from '../models/AccessToken';
import { FreightDocument } from '../models/freight-document/FreightDocument';
import { Comment } from '../models/freight-document/Comment';
import { CompanyQrCodeSignature, OwnApprovalSignature, OwnSogApprovalSignature, PairedSignOnGlass } from '../models/freight-document/Signatures';
import { shareReplay, tap } from 'rxjs/operators';
import { Reference } from '../models/freight-document/Reference';
import { DateUtils } from '../utils/DateUtils';
import { StructuredGood } from '../models/freight-document/StructuredGood';
import { LicensePlate } from '../models/freight-document/LicensePlate';
import { Event } from '../models/freight-document/Event';
import { StructuredGoodsInfo } from '../models/freight-document/StructuredGoodsInfo';
import { FreightDocumentDeca } from '../models/freight-document/FreightDocumentDeca';

@Injectable({
  providedIn: 'root'
})
export class SigningService {
  ACCESS_CODE_STORAGE_KEY = 'carrierAccessCode';
  private oauthToken: AccessToken = null;
  private readonly decaDocumentsCache = new Map<string, Observable<FreightDocumentDeca[]>>();

  constructor(private httpClient: HttpClient) {
  }

  authenticate(accessCode: string): Observable<AccessToken> {
    return this.authenticateByAccessCode(accessCode).pipe(
      tap((response: AccessToken) => {
        this.oauthToken = response;
        this.storeAccessCode(accessCode);
      })
    );
  }

  authenticateByAccessCode(accessCode: string): Observable<AccessToken> {
    const params = new HttpParams().append('code', accessCode).append('grant_type', 'access_code');
    return this.httpClient.post<AccessToken>('/oauth/token', params);
  }

  loadDocumentsByAccessCode(): Observable<FreightDocument[]> {
    return this.httpClient.get<FreightDocument[]>('/freightdocuments/accesscodesearch', this.getOptions());
  }

  get(id: string): Observable<FreightDocument> {
    return this.httpClient.get<FreightDocument>(`/portal/freightdocuments/${id}`, this.getOptions());
  }

  getDecaDocuments(freightDocumentId: string): Observable<FreightDocumentDeca[]> {
    if (!this.decaDocumentsCache.has(freightDocumentId)) {
      this.decaDocumentsCache.set(
        freightDocumentId,
        this.httpClient.get<FreightDocumentDeca[]>(`/freightdocuments/${freightDocumentId}/deca`, this.getOptions())
          .pipe(shareReplay(1)));
    }
    return this.decaDocumentsCache.get(freightDocumentId);
  }

  addComment(fdId: string, comment: Comment): Observable<void> {
    return this.httpClient.post<void>(`/freightdocuments/${fdId}/comments`, comment, this.getOptions());
  }

  addStructuredGood(fdId: string, structuredGood: StructuredGood): Observable<void> {
    const deviceTimeUploaded = Date.now();
    const body = { structuredGood, deviceTimeUploaded, deviceTimeCreated: deviceTimeUploaded };
    return this.httpClient.post<void>(`/freightdocuments/${fdId}/structuredgoods`, body, this.getOptions());
  }

  updateGoods(fdId: string, structuredGoods: StructuredGood[]): Observable<void> {
    const deviceTimeUploaded = Date.now();
    const body = { structuredGoods, deviceTimeUploaded, deviceTimeCreated: deviceTimeUploaded };
    return this.httpClient.put<void>(`/freightdocuments/${fdId}/structuredgoods`, body, this.getOptions());
  }

  updateLicensePlates(fdId: string, roleId: string, licensePlates: LicensePlate[]): Observable<void> {
    const deviceTimeUploaded = DateUtils.getUTCDateTime(new Date());
    const body = { roleId, licensePlates, deviceTimeUploaded, deviceTimeCreated: deviceTimeUploaded };
    return this.httpClient.put<void>(`/freightdocuments/${fdId}/licenseplates`, body, this.getOptions());
  }

  registerEvent(fdId: string, event: Event): Observable<void> {
    return this.httpClient.post<void>(`/freightdocuments/${fdId}/events`, event, this.getOptions());
  }

  signWithPairedSog(fdId: string, body: PairedSignOnGlass): Observable<void> {
    return this.httpClient.post<void>(`/freightdocuments/${fdId}/transfergoodswithpairedsignonglass`, body, this.getOptions());
  }

  signWithCompanyQrCode(fdId: string, body: CompanyQrCodeSignature): Observable<void> {
    return this.httpClient.post<void>(`/freightdocuments/${fdId}/transfergoodswithidcode`, body, this.getOptions());
  }

  signWithUploadDocument(fdId: string, body: OwnApprovalSignature): Observable<void> {
    return this.httpClient.post<void>(`/freightdocuments/${fdId}/transfergoodswithownapproval`, body, this.getOptions());
  }

  signWithOwnSogApproval(fdId: string, body: OwnSogApprovalSignature): Observable<void> {
    return this.httpClient.post<void>(`/freightdocuments/${fdId}/transfergoodswithpairedsignonglass`, body, this.getOptions());
  }

  notifyMessenger(transportId: string, messengerUserId: string, signedFreightDocumentIds: string[]) {
    const body = {
      transportId: transportId,
      messengerUserId: messengerUserId,
      freightDocumentIds: signedFreightDocumentIds
    };
    return this.httpClient.post('/bot/process/signing', body, this.getOptions());
  }

  updateReferences(freightDocumentId: string, references: Reference[]) {
    const date = DateUtils.getUTCDateTime(new Date());
    return this.patchFreightDocument(freightDocumentId, {
      deviceTimeCreated: date,
      deviceTimeUploaded: date,
      references: references
    });
  }

  addSeals(freightDocumentId: string, seals: string) {
    const date = DateUtils.getUTCDateTime(new Date());
    return this.patchFreightDocument(freightDocumentId, {
      deviceTimeCreated: date,
      deviceTimeUploaded: date,
      structuredGoodsInfo: { sealNumbers: seals }
    });
  }

  updateGoodsInfo(freightDocumentId: string, structuredGoodsInfo: StructuredGoodsInfo) {
    const date = DateUtils.getUTCDateTime(new Date());
    return this.patchFreightDocument(freightDocumentId, {
      deviceTimeCreated: date,
      deviceTimeUploaded: date,
      structuredGoodsInfo: structuredGoodsInfo
    });
  }

  getAttachment(attachmentId: string, contentType = 'image/png'): Observable<Blob> {
    const options = this.getOptions();
    const headers = options.headers.set('content-type', contentType);
    return this.httpClient.get(`/attachments/${attachmentId}/content`, { headers, responseType: 'blob' });
  }

  private patchFreightDocument(freightDocumentId: string, body: FreightDocumentPatchRequest) {
    return this.httpClient.patch(`/portal/freightdocuments/${freightDocumentId}`, body, this.getOptions());
  }

  public getLastAccessCodeUsed(): string {
    return localStorage.getItem(this.ACCESS_CODE_STORAGE_KEY);
  }

  private storeAccessCode(accessCode: string): void {
    return localStorage.setItem(this.ACCESS_CODE_STORAGE_KEY, accessCode);
  }

  private getOptions(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this.oauthToken.access_token}`)
    };
  }
}

export enum SigningAction {
  COLLECTION = 'COLLECTION',
  DELIVERY = 'DELIVERY',
  LEAVE_AT_INTERMEDIATE = 'LEAVE_AT_INTERMEDIATE',
  PICKUP_FROM_INTERMEDIATE = 'PICKUP_FROM_INTERMEDIATE'
}

export interface FreightDocumentPatchRequest {
  deviceTimeCreated: string,
  deviceTimeUploaded: string,
  references?: Reference[],
  structuredGoodsInfo?: Partial<StructuredGoodsInfo>
}
